import { useId, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowsLeftRightIcon,
  ConfirmDeletionModal,
  CopyIcon,
  getTaskTitle,
  Mtds,
  PencilSimpleIcon,
  SinglePlan,
  Task,
  TrashIcon,
  useDeleteTaskMutation,
  useErrorToast,
  useGetTaskByIdQuery
} from 'prove-common'
import EditTaskCard from '../edit-task-card/EditTaskCard'
import CopyTaskModal from '../copy-task-modal/CopyTaskModal'

type TaskActionsMenuProps = {
  task?: Task
  delegationId?: number
  plan: SinglePlan
  buttonId?: string
}

export const TaskActionsMenu = ({
  task,
  delegationId,
  plan,
  buttonId
}: TaskActionsMenuProps) => {
  const menuId = useId()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const deleteTask = useDeleteTaskMutation()
  const showError = useErrorToast('slette oppgaven')
  const [shouldFetchTask, setShouldFetchTask] = useState(false)
  const fetchedTask = useGetTaskByIdQuery(
    shouldFetchTask ? (task?.id ?? delegationId) : undefined
  )
  const selectedTask = fetchedTask.data
  const [isOpenEditTaskModal, setOpenEditTaskModal] = useState(false)
  const [isOpenCopyTaskModal, setOpenCopyTaskModal] = useState(false)
  const [isOpenDeleteTaskModal, setOpenDeleteTaskModal] = useState(false)
  const taskTitle = selectedTask ? getTaskTitle(selectedTask) : 'oppgaven'
  const isTaskLoading =
    shouldFetchTask && selectedTask == null && fetchedTask.isFetching
  const isTaskUnavailable = selectedTask == null

  return (
    <>
      <Mtds.Button
        id={buttonId}
        type='button'
        aria-label='Handlingsmeny'
        popoverTarget={menuId}
        onClick={(event) => {
          event.stopPropagation()
          setShouldFetchTask(true)
        }}
      />
      <Mtds.Popover as='menu' id={menuId}>
        {isTaskLoading ? (
          <li>
            <Mtds.Flex data-align='center' data-gap={2}>
              <Mtds.Spinner data-size='sm' />
              Laster
            </Mtds.Flex>
          </li>
        ) : (
          <>
            <li>
              <Mtds.Button
                onClick={() => setOpenEditTaskModal(true)}
                disabled={isTaskUnavailable || !selectedTask?.canManage}
              >
                <PencilSimpleIcon />
                Rediger
              </Mtds.Button>
            </li>
            <li>
              <Mtds.Button
                onClick={() =>
                  selectedTask &&
                  navigate(`/plans/${plan.id}/tasks/${selectedTask.id}/move`)
                }
                disabled={isTaskUnavailable || !selectedTask?.canManage}
              >
                <ArrowsLeftRightIcon />
                Flytt
              </Mtds.Button>
            </li>
            <li>
              <Mtds.Button
                onClick={() => setOpenCopyTaskModal(true)}
                disabled={isTaskUnavailable}
              >
                <CopyIcon />
                Kopier
              </Mtds.Button>
            </li>
            <li>
              <Mtds.Button
                data-color='danger'
                onClick={() => setOpenDeleteTaskModal(true)}
                disabled={isTaskUnavailable || !selectedTask?.canManage}
              >
                <TrashIcon />
                Slett
              </Mtds.Button>
            </li>
          </>
        )}
      </Mtds.Popover>
      {selectedTask && (
        <>
          <EditTaskCard
            isOpen={isOpenEditTaskModal}
            setOpen={setOpenEditTaskModal}
            task={selectedTask}
            plan={plan}
          />
          <CopyTaskModal
            isOpen={isOpenCopyTaskModal}
            setOpen={setOpenCopyTaskModal}
            task={selectedTask}
            destinationPlanId={plan.id}
          />
        </>
      )}
      <ConfirmDeletionModal
        setOpen={() => setOpenDeleteTaskModal(false)}
        isOpen={isOpenDeleteTaskModal}
        onConfirm={async () => {
          if (!selectedTask) return
          await deleteTask
            .mutateAsync({ taskId: selectedTask.id, etag: selectedTask.etag })
            .then(async () => {
              Mtds.toast.success('Oppgaven er slettet')
              await queryClient.invalidateQueries({ queryKey: ['plans'] })
              navigate(`/plans/${plan.id}`)
            })
            .catch((error) => {
              showError(error)
            })
        }}
        title={`Oppgaven «${taskTitle}» vil bli permanent slettet.`}
        isPending={deleteTask.isPending}
      />
    </>
  )
}
